import { BootResponse, OfferAPIResponse } from '@appcharge/shared-ui';
import { ESaleDiscountDisplayType, OfferType } from 'constants/enums';

import { DEFAULT_PERSONALIZATION_PAYLOAD } from '../../TestPersonalization/defaults';

const DEFAULT_ENDS_IN_MS = 259200000;

type OfferAPIResponseForEnrichment = OfferAPIResponse & {
  playerLevelName?: string;
  offersOrder?: string;
};

function normalizeBadgeForPayload(badge: any): Record<string, unknown> | null {
  if (!badge) return null;
  const publisherBadgeId =
    badge.publisherBadgeId ?? badge.badge?.publisherBadgeId ?? '';
  if (!publisherBadgeId) return null;
  const position = badge.position ?? badge.badge?.position;
  const ribbonTextOverride =
    badge.ribbonTextOverride ?? badge.badge?.ribbon?.text ?? '';
  return {
    publisherBadgeId,
    ...(position != null && { position }),
    ...(ribbonTextOverride !== '' && { ribbonTextOverride })
  };
}

function toPlayerAvailabilityPayload(
  pa: number | { total?: number; remaining?: number } | null | undefined
): { total: number; remaining: number } {
  if (pa == null) return { total: 0, remaining: 0 };
  return typeof pa === 'number'
    ? { total: pa, remaining: pa }
    : { total: pa.total ?? 0, remaining: pa.remaining ?? 0 };
}

function mapOfferToOverrideFormat(offer: any): Record<string, unknown> {
  const result: Record<string, unknown> = {
    publisherOfferId: offer.publisherOfferId
  };

  const offerDesignId =
    offer.offerUiId ?? offer.offerUi?.offerUiId ?? offer.offerUi?._id ?? '';
  if (offerDesignId) {
    result.offerDesignId = offerDesignId;
  }
  if (offer.displayName != null && String(offer.displayName).trim() !== '') {
    result.offerDesignOverride = {
      ...(offerDesignId && { offerDesignId }),
      offerDesignSubtitleTextOverride: offer.displayName
    };
  }
  if (offer.description != null && String(offer.description).trim() !== '') {
    result.offerDescriptionOverride = offer.description;
  }

  const priceDiscount =
    offer.priceDiscount ?? offer.productsSequence?.[0]?.priceDiscount;
  if (priceDiscount && (priceDiscount.discount ?? 0) > 0) {
    const priceInCents =
      offer.productsSequence?.[0]?.priceInUsdCents ??
      offer.priceInUsdCents ??
      0;
    const discount = priceDiscount.discount ?? 0;
    const type =
      priceDiscount.type ?? ESaleDiscountDisplayType.PERCENTAGE;
    const priceBeforeDiscount =
      type === ESaleDiscountDisplayType.PERCENTAGE
        ? Math.round(priceInCents / (1 - discount / 100))
        : priceInCents + discount;
    result.priceDiscount = {
      priceBeforeDiscount,
      discount,
      type
    };
  }

  if (offer.productSale != null && (offer.productSale.sale ?? 0) > 0) {
    const sale = offer.productSale.sale ?? 0;
    const saleType = offer.productSale.type ?? 'percentage';
    result.productSale = {
      amountBeforeSale: offer.productsSequence?.[0]?.priceInUsdCents ?? offer.priceInUsdCents ?? 0,
      sale,
      type: saleType
    };
  }

  if (offer.playerAvailability != null) {
    result.playerAvailability = toPlayerAvailabilityPayload(
      offer.playerAvailability
    );
  }

  if (Array.isArray(offer.badges) && offer.badges.length > 0) {
    const badgesPayload = offer.badges
      .map(normalizeBadgeForPayload)
      .filter(Boolean);
    if (badgesPayload.length > 0) {
      result.badges = badgesPayload;
    }
  }

  result.endsIn =
    typeof offer.endsIn === 'number' && offer.endsIn > 0
      ? offer.endsIn
      : Date.now() + DEFAULT_ENDS_IN_MS;

  const hasDynamicOfferUi =
    offer.dynamicOfferUi != null && typeof offer.dynamicOfferUi === 'object';
  const hasBadgesOrSale =
    (Array.isArray(offer.badges) && offer.badges.length > 0) ||
    (offer.productSale?.sale ?? 0) > 0;
  if (hasDynamicOfferUi || hasBadgesOrSale) {
    result.dynamicOfferUi = {
      badges: hasDynamicOfferUi
        ? (offer.dynamicOfferUi.badges ?? [])
        : (offer.badges ?? []).map(normalizeBadgeForPayload).filter(Boolean),
      salePercentage:
        (hasDynamicOfferUi
          ? offer.dynamicOfferUi.salePercentage
          : null) ?? offer.productSale?.sale ?? 0,
      amountBeforeSale:
        (hasDynamicOfferUi
          ? offer.dynamicOfferUi.amountBeforeSale
          : null) ??
        offer.productsSequence?.[0]?.priceInUsdCents ??
        offer.priceInUsdCents ??
        0,
      salePercentageDisplayType: hasDynamicOfferUi
        ? offer.dynamicOfferUi.salePercentageDisplayType ?? 'percentage'
        : (offer.productSale?.type === 'percentage' ? 'percentage' : 'value'),
      offerDesignId:
        (hasDynamicOfferUi
          ? offer.dynamicOfferUi.offerDesignId
          : null) ?? (offerDesignId || undefined)
    };
  }

  const productsSequence = Array.isArray(offer.productsSequence)
    ? offer.productsSequence.map((seq: any) => {
        const item: Record<string, unknown> = {
          index: Math.max(1, seq.index ?? 0),
          products: (seq.products ?? []).map((p: any) => {
            const prod = typeof p.product === 'object' && p.product != null ? p.product : p;
            const publisherProductId =
              p.publisherProductId ?? prod.publisherProductId;
            const quantity =
              typeof p.quantity === 'number'
                ? p.quantity
                : typeof p.quantity === 'string'
                  ? Number(p.quantity) || 0
                  : typeof prod.quantity === 'number'
                    ? prod.quantity
                    : Number(prod.quantity) || 0;
            const priority = p.priority ?? prod.priority;
            const product: Record<string, unknown> = {
              publisherProductId,
              quantity,
              ...(priority && { priority })
            };
            const traits = p.traits ?? prod.traits;
            if (Array.isArray(traits) && traits.length > 0) {
              product.traits = traits.map((t: any) => ({
                ...(t.assetId != null && { assetId: t.assetId }),
                ...(t.assetUrl != null && { assetUrl: t.assetUrl }),
                text: t.text ?? ''
              }));
            }
            const rarityProductInfo = p.rarityProductInfo ?? prod.rarityProductInfo;
            if (rarityProductInfo != null) {
              product.rarityProductInfo = rarityProductInfo;
            }
            const badgeText = p.badgeText ?? prod.badgeText;
            if (badgeText != null && String(badgeText).trim() !== '') {
              product.badgeText = badgeText;
            }
            return product;
          })
        };
        if (
          Array.isArray(seq.progressBarPoints) &&
          seq.progressBarPoints.length > 0
        ) {
          const validProgressBarPoints = seq.progressBarPoints.filter(
            (pb: any) =>
              typeof pb?.publisherBarId === 'string' &&
              pb.publisherBarId.trim() !== ''
          );
          if (validProgressBarPoints.length > 0) {
            item.progressBarPoints = validProgressBarPoints;
          }
        }
        if (seq.playerAvailability != null) {
          item.playerAvailability = toPlayerAvailabilityPayload(
            seq.playerAvailability
          );
        }
        if (Array.isArray(seq.badges) && seq.badges.length > 0) {
          const stepBadges = seq.badges
            .map(normalizeBadgeForPayload)
            .filter(Boolean);
          if (stepBadges.length > 0) {
            item.badges = stepBadges;
          }
        }
        if (seq.productSale != null && (seq.productSale.sale ?? 0) > 0) {
          item.productSale = {
            amountBeforeSale: seq.priceInUsdCents ?? 0,
            sale: seq.productSale.sale ?? 0,
            type: seq.productSale.type ?? 'percentage'
          };
        }
        if (seq.priceDiscount != null && (seq.priceDiscount.discount ?? 0) > 0) {
          item.priceDiscount = {
            priceBeforeDiscount:
              seq.priceInUsdCents != null
                ? Math.round(
                    seq.priceInUsdCents /
                      (1 - (seq.priceDiscount.discount ?? 0) / 100)
                  )
                : 0,
            discount: seq.priceDiscount.discount ?? 0,
            type: seq.priceDiscount.type ?? 'percentage'
          };
        }
        return item;
      })
    : [];

  result.productsSequence = productsSequence;

  return result;
}

function isExistingOffer(offer: any): boolean {
  return !!(
    offer?.publisherOfferId && String(offer.publisherOfferId).trim()
  );
}

export function buildEnrichedOffersPayload(
  boot: BootResponse,
  generatedOffers: OfferAPIResponseForEnrichment
): Record<string, unknown> {
  const { offers } = generatedOffers;
  const offersArray = [
    offers.progressBars,
    offers.popups,
    offers.promotions,
    offers.bundles,
    offers.rolling
  ]
    .flatMap((arr) => arr ?? [])
    .filter(isExistingOffer)
    .map((offer: any) => mapOfferToOverrideFormat(offer));

  const balances = generatedOffers.balances.map((balance: any) => ({
    publisherProductId:
      balance.product?.publisherProductId ?? balance.publisherProductId,
    quantity: balance.quantity
  }));

  const general = boot.storeTheme?.general;
  const endsIn = Date.now() + DEFAULT_ENDS_IN_MS;
  const playerLevel = {
    assetId: general?.playerLevelInternalViewModel ?? '',
    text: generatedOffers.playerLevelName ?? '',
    endsIn
  };

  const sectionsFromOffers = generatedOffers.sections as
    | Array<{ publisherSectionId?: string }>
    | undefined;
  const sectionsFromBoot = general?.sections as
    | Array<{ publisherSectionId?: string }>
    | undefined;
  const sectionsOrder = Array.isArray(sectionsFromOffers)
    ? sectionsFromOffers.map((s) => s.publisherSectionId).filter(Boolean)
    : Array.isArray(sectionsFromBoot)
      ? sectionsFromBoot.map((s) => s.publisherSectionId).filter(Boolean)
      : [];
  const playerLevelBanners = normalizePlayerLevelBanners(
    generatedOffers.playerLevels
  );
  const generatedOffersAny = generatedOffers as OfferAPIResponse & { bannerImage?: string };
  const payload: Record<string, unknown> = {
    ...DEFAULT_PERSONALIZATION_PAYLOAD,
    logo: general?.logo ?? '',
    bannerExternalId: general?.bannerInternalViewModel ?? '',
    sectionsOrder,
    focus: {
      publisherBundleId: generatedOffers.offerFocus?.bundleId ?? ''
    },
    ...(generatedOffers.offersOrder?.trim() && {
      offersOrder: generatedOffers.offersOrder.trim()
    }),
    storeTheme: {
      bgImageMobile: general?.backgroundImageMobile ?? '',
      bgImageDesktop: general?.backgroundImageDesktop ?? '',
      logo: general?.logo ?? '',
      profileFrameImage: generatedOffers.profileFrameImg ?? '',
      bannerImage:
        generatedOffersAny.bannerImage ?? general?.bannerInternalViewModel ?? '',
      playerLevelImage: generatedOffers.playerLevelImg ?? ''
    },
    balances,
    offers: offersArray,
    playerLevelBanners,
    playerLevel,
    segments: Array.isArray(generatedOffers.segments)
      ? generatedOffers.segments
      : []
  };
  return payload;
}

function normalizePlayerLevelText(
  raw: unknown
): Array<{ id: string; content: string }> {
  if (Array.isArray(raw)) {
    return raw.map((t: any, i: number) =>
      typeof t === 'object' && t != null && 'id' in t && 'content' in t
        ? { id: t.id, content: t.content }
        : { id: `text_${i}`, content: typeof t === 'string' ? t : String(t) }
    );
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    return [{ id: 'title', content: raw }];
  }
  return [];
}

function normalizePlayerLevelBanners(
  playerLevels: unknown[] | undefined
): Array<{ assetId: string; designId: string; text: Array<{ id: string; content: string }>; endsIn: number }> {
  if (!Array.isArray(playerLevels) || playerLevels.length === 0) return [];
  return playerLevels.map((item: any) => ({
    assetId: item.assetId ?? item.assetUrl ?? '',
    designId: item.designId ?? '',
    text: normalizePlayerLevelText(item.text),
    endsIn:
      typeof item.endsIn === 'number' && item.endsIn > 0
        ? item.endsIn
        : Date.now() + DEFAULT_ENDS_IN_MS
  }));
}

function mergeBalances(
  generatedBalances: OfferAPIResponse['balances'],
  parsedBalances: unknown
): OfferAPIResponse['balances'] {
  const generated = generatedBalances ?? [];
  const parsed = Array.isArray(parsedBalances) ? parsedBalances : [];

  const safeDefaultImages = { product: '', productPrefix: '' };

  return generated.map((genBalance: any, index: number) => {
    const parsedItem = parsed[index] as Record<string, unknown> | undefined;
    const productId =
      parsedItem?.publisherProductId ??
      genBalance?.product?.publisherProductId ??
      genBalance?.publisherProductId;
    const quantity =
      parsedItem?.quantity != null
        ? String(parsedItem.quantity)
        : genBalance?.quantity ?? '0';
    const formattedAmount =
      typeof parsedItem?.formattedAmount === 'string'
        ? parsedItem.formattedAmount
        : genBalance?.formattedAmount ?? quantity;

    const product = genBalance?.product ?? {};
    const images =
      genBalance?.images && typeof genBalance.images === 'object'
        ? {
            product: genBalance.images.product ?? '',
            productPrefix: genBalance.images.productPrefix ?? ''
          }
        : safeDefaultImages;

    return {
      product: { ...product, publisherProductId: productId },
      quantity,
      formattedAmount,
      images
    };
  });
}

function mergeProductsInSequence(
  generatedProducts: any[] | undefined,
  parsedProducts: unknown
): any[] {
  const parsed = Array.isArray(parsedProducts) ? parsedProducts : [];
  const generated = Array.isArray(generatedProducts) ? generatedProducts : [];
  if (parsed.length === 0) return generated;
  return parsed.map((parsedProd: any, j: number) => {
    const genProd = generated[j];
    return {
      ...(genProd ?? {}),
      ...parsedProd,
      product: genProd?.product ?? parsedProd?.product
    };
  });
}

function mergeProductsSequence(
  generatedSequence: any[] | undefined,
  parsedSequence: unknown
): any[] | undefined {
  const parsed = Array.isArray(parsedSequence) ? parsedSequence : [];
  const generated = Array.isArray(generatedSequence) ? generatedSequence : [];
  if (parsed.length === 0) return generated.length > 0 ? generated : undefined;
  return parsed.map((parsedItem: any, i: number) => {
    const genItem = generated[i] ?? {};
    const mergedProducts = mergeProductsInSequence(
      genItem.products,
      parsedItem.products
    );
    return {
      ...genItem,
      ...parsedItem,
      products: mergedProducts.length > 0 ? mergedProducts : genItem.products ?? parsedItem.products ?? []
    };
  });
}

function mergeOfferWithGenerated(
  parsedOffer: Record<string, unknown>,
  generatedOffer: any
): any {
  if (!generatedOffer) return parsedOffer;
  const mergedProductsSequence = mergeProductsSequence(
    generatedOffer.productsSequence,
    parsedOffer.productsSequence
  );
  return {
    ...generatedOffer,
    ...parsedOffer,
    offerUi: generatedOffer.offerUi ?? parsedOffer.offerUi,
    productsSequence:
      mergedProductsSequence ?? parsedOffer.productsSequence ?? generatedOffer.productsSequence
  };
}

function mergeOffersArrayWithGenerated(
  parsedCategory: any[],
  generatedCategory: any[] | undefined
): any[] {
  const generated = Array.isArray(generatedCategory) ? generatedCategory : [];
  return parsedCategory.map((parsed: any, index: number) =>
    mergeOfferWithGenerated(parsed, generated[index])
  );
}

export function mergePersonalizationWithOffers(
  parsedPersonalization: Record<string, unknown>,
  generatedOffers: OfferAPIResponse
): OfferAPIResponse {
  let mergedOffers = generatedOffers.offers;

  if (parsedPersonalization.offers !== undefined) {
    const parsedOffers = parsedPersonalization.offers;
    if (Array.isArray(parsedOffers) && parsedOffers.length > 0) {
      const progressBars = parsedOffers.filter(
        (o: any) =>
          o.type === OfferType.PROGRESS_BAR ||
          o.offerUi?.offerUiType === OfferType.PROGRESS_BAR
      );
      const popups = parsedOffers.filter(
        (o: any) =>
          o.offerType === OfferType.POPUP ||
          o.offerUi?.offerUiType === OfferType.POPUP
      );
      const promotions = parsedOffers.filter(
        (o: any) =>
          o.offerType === OfferType.SPECIAL_OFFER ||
          o.offerUi?.offerUiType === OfferType.SPECIAL_OFFER
      );
      const bundles = parsedOffers.filter(
        (o: any) =>
          o.offerType === OfferType.BUNDLE ||
          o.offerUi?.offerUiType === OfferType.BUNDLE
      );
      const rolling = parsedOffers.filter(
        (o: any) =>
          o.offerType === OfferType.ROLLING_OFFER ||
          o.offerUi?.offerUiType === OfferType.ROLLING_OFFER
      );
      mergedOffers = {
        progressBars: mergeOffersArrayWithGenerated(
          progressBars,
          generatedOffers.offers?.progressBars
        ),
        popups: mergeOffersArrayWithGenerated(
          popups,
          generatedOffers.offers?.popups
        ),
        promotions: mergeOffersArrayWithGenerated(
          promotions,
          generatedOffers.offers?.promotions
        ),
        bundles: mergeOffersArrayWithGenerated(
          bundles,
          generatedOffers.offers?.bundles
        ),
        rolling: mergeOffersArrayWithGenerated(
          rolling,
          generatedOffers.offers?.rolling
        )
      };
    } else if (
      !Array.isArray(parsedOffers) &&
      typeof parsedOffers === 'object'
    ) {
      const gen = generatedOffers.offers;
      const po = parsedOffers as OfferAPIResponse['offers'];
      mergedOffers = {
        progressBars: mergeOffersArrayWithGenerated(
          po.progressBars ?? [],
          gen?.progressBars
        ),
        popups: mergeOffersArrayWithGenerated(po.popups ?? [], gen?.popups),
        promotions: mergeOffersArrayWithGenerated(
          po.promotions ?? [],
          gen?.promotions
        ),
        bundles: mergeOffersArrayWithGenerated(po.bundles ?? [], gen?.bundles),
        rolling: mergeOffersArrayWithGenerated(po.rolling ?? [], gen?.rolling)
      };
    }
  }

  const mergedBalances = mergeBalances(
    generatedOffers.balances,
    parsedPersonalization.balances
  );

  return {
    ...generatedOffers,
    ...parsedPersonalization,
    offers: mergedOffers,
    balances: mergedBalances
  } as OfferAPIResponse;
}
